import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import PageShell from "@/components/PageShell";
import { CAREERS } from "@/constants/testIds";
import { getCareers } from "@/lib/api";

export default function CareersPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getCareers()
            .then((rows) => setItems(Array.isArray(rows) ? rows : []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <PageShell>
            <section
                data-testid={CAREERS.section}
                className="py-24 md:py-36"
            >
                <div className="mx-auto max-w-[1800px] px-6 md:px-12 lg:px-20">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16 md:mb-24">
                        <div className="col-span-12 md:col-span-3">
                            <p className="eyebrow text-[#1B1B1B]/70">
                                § Careers
                            </p>
                        </div>
                        <div className="col-span-12 md:col-span-9">
                            <motion.h1
                                data-testid={CAREERS.heading}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                                className="font-serif-editorial text-5xl md:text-7xl lg:text-[104px] leading-[0.98] tracking-[-0.02em] text-[#1B1B1B] max-w-5xl"
                            >
                                Come build things that{" "}
                                <em className="italic text-[#A8844F]">last.</em>
                            </motion.h1>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 1, delay: 0.2 }}
                                className="mt-10 md:mt-14 space-y-6 max-w-2xl"
                            >
                                <p className="font-sans-tight text-base md:text-lg leading-[1.7] text-[#1B1B1B]/85 font-light">
                                    We hire rarely, and we hire carefully. A
                                    small studio means every person shapes the
                                    work directly — there is nowhere to hide,
                                    and nothing you make gets buried under three
                                    layers of approval.
                                </p>
                                <p className="font-sans-tight text-base md:text-lg leading-[1.7] text-[#1B1B1B]/85 font-light">
                                    If you care about craft more than speed, and
                                    you would rather do a few things properly
                                    than many things quickly, we would like to
                                    hear from you — whether or not a role below
                                    matches what you do.
                                </p>
                            </motion.div>
                        </div>
                    </div>

                    {/* Open positions */}
                    <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10">
                        <div className="col-span-12 md:col-span-3">
                            <p className="eyebrow text-[#1B1B1B]/70">
                                Open Positions
                            </p>
                        </div>
                        <div className="col-span-12 md:col-span-9">
                            {loading ? (
                                <p className="eyebrow text-[#1B1B1B]/50">
                                    Loading…
                                </p>
                            ) : items.length === 0 ? (
                                <div data-testid={CAREERS.empty}>
                                    <p className="font-serif-editorial italic text-3xl md:text-4xl text-[#1B1B1B]/75 leading-[1.2] max-w-2xl">
                                        No open positions at the moment.
                                    </p>
                                    <p className="mt-6 font-sans-tight text-base leading-[1.7] text-[#1B1B1B]/80 font-light max-w-xl">
                                        We still read every note that arrives.
                                        If you think you belong here, write to
                                        us and show us what you have made.
                                    </p>
                                    <a
                                        href="mailto:careers@depmont.com"
                                        className="mt-8 inline-flex items-center gap-3 px-7 py-[14px] border border-[#1B1B1B] text-[#1B1B1B] font-sans-tight text-[12px] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#1B1B1B] hover:text-[#F6F3EC]"
                                    >
                                        Introduce yourself
                                    </a>
                                </div>
                            ) : (
                                <div className="border-t border-[#E6E2D6]">
                                    {items.map((job, i) => (
                                        <motion.article
                                            key={job.id}
                                            data-testid={CAREERS.position(job.id)}
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true, margin: "-8%" }}
                                            transition={{
                                                duration: 0.9,
                                                delay: i * 0.05,
                                                ease: [0.16, 1, 0.3, 1],
                                            }}
                                            className="group grid grid-cols-12 gap-4 md:gap-8 py-10 md:py-14 border-b border-[#E6E2D6]"
                                        >
                                            <div className="col-span-2 md:col-span-1">
                                                <span className="font-sans-tight text-xs tracking-[0.22em] text-[#1B1B1B]/60 mt-2 inline-block">
                                                    /0{i + 1}
                                                </span>
                                            </div>
                                            <div className="col-span-10 md:col-span-6">
                                                <h2 className="font-serif-editorial text-3xl md:text-4xl lg:text-5xl text-[#1B1B1B] leading-[1.05]">
                                                    {job.title}
                                                </h2>
                                                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                                                    {job.department && (
                                                        <span className="eyebrow text-[#1B1B1B]/60">
                                                            {job.department}
                                                        </span>
                                                    )}
                                                    <span className="eyebrow text-[#1B1B1B]/60">
                                                        {job.location}
                                                    </span>
                                                    <span className="eyebrow text-[#A8844F]">
                                                        {job.employment_type}
                                                    </span>
                                                </div>
                                                {job.description && (
                                                    <p className="mt-5 font-sans-tight text-base leading-[1.7] text-[#1B1B1B]/85 font-light max-w-xl whitespace-pre-wrap">
                                                        {job.description}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="col-span-12 md:col-span-5 flex md:justify-end md:items-start">
                                                <a
                                                    href={`mailto:${job.apply_email}?subject=${encodeURIComponent(`Application — ${job.title}`)}`}
                                                    className="inline-flex items-center gap-3 px-7 py-[14px] bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[12px] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#A8844F]"
                                                >
                                                    Apply
                                                    <svg
                                                        className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="1.2"
                                                    >
                                                        <path d="M1 8h14M9 2l6 6-6 6" />
                                                    </svg>
                                                </a>
                                            </div>
                                        </motion.article>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Foot note */}
                    <div className="mt-20 md:mt-28 border-t border-[#E6E2D6] pt-8 flex flex-col md:flex-row justify-between gap-4">
                        <p className="eyebrow text-[#1B1B1B]/70">
                            {items.length > 0
                                ? `${items.length} open ${items.length === 1 ? "position" : "positions"}`
                                : "Applications always welcome"}
                        </p>
                        <a
                            href="mailto:careers@depmont.com"
                            className="link-underline font-sans-tight text-sm text-[#1B1B1B]"
                        >
                            careers@depmont.com →
                        </a>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
